"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import type { GoogleReview } from "@/lib/google-reviews";
import { useMediaQuery } from "@/lib/use-media-query";
import { useOpenWithFocus } from "@/lib/use-open-with-focus";

const TRUNCATE_AT = 170;

function relativeTime(timestamp: number): string {
  if (!timestamp) return "";
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;
  const days = Math.floor(diff / 86400);
  if (days < 1) return "today";
  if (days === 1) return "a day ago";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return "a week ago";
  if (weeks < 5) return `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "a month ago";
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  if (years === 1) return "a year ago";
  return `${years} years ago`;
}

const noopSubscribe = () => () => {};

export function RelativeTime({ timestamp }: { timestamp: number }) {
  return useSyncExternalStore(
    noopSubscribe,
    () => relativeTime(timestamp),
    () => "",
  );
}

export function formatName(name: string) {
  return name
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function StarRating({ rating, large }: { rating: number; large?: boolean }) {
  return (
    <div role="img" className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          className={`${large ? "size-5" : "size-4"} ${i < rating ? "text-yellow-400" : "text-black/15"}`}
          fill="currentColor"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export function GoogleMark({ large, label = true }: { large?: boolean; label?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <svg viewBox="0 0 48 48" className={large ? "size-5" : "size-4"} aria-hidden>
        <path
          fill="#FFC107"
          d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
        />
        <path
          fill="#FF3D00"
          d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
        />
      </svg>
      {label ? (
        <span className={`${large ? "text-sm" : "text-[10px]"} text-black/70`}>Google Review</span>
      ) : null}
    </div>
  );
}

function ReviewHeader({
  review,
  large,
  avatarClass = "bg-navy-01",
}: {
  review: GoogleReview;
  large?: boolean;
  avatarClass?: string;
}) {
  return (
    <>
      <div className="flex items-center gap-3">
        {review.authorImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={`/api/avatar/?u=${encodeURIComponent(review.authorImage)}`}
            alt={review.authorName}
            aria-hidden
            width={48}
            height={48}
            loading="lazy"
            decoding="async"
            className={`${large ? "size-12" : "size-10"} shrink-0 rounded-full object-cover`}
          />
        ) : (
          <span
            className={`flex shrink-0 items-center justify-center rounded-full font-medium text-white ${avatarClass} ${large ? "size-12 text-xl" : "size-10 text-base"}`}
          >
            {review.authorName.charAt(0).toUpperCase()}
          </span>
        )}
        <span
          className={`line-clamp-2 min-w-0 flex-1 leading-tight font-bold text-black ${large ? "text-h6" : "text-[16px]"}`}
          title={formatName(review.authorName)}
        >
          {formatName(review.authorName)}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <StarRating rating={review.rating} large={large} />
        <span className={`${large ? "text-sm" : "text-xs"} text-black/70`}>
          <RelativeTime timestamp={review.time} />
        </span>
      </div>
    </>
  );
}

function ReviewScrollText({ text, scrollClass }: { text: string; scrollClass: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);

  return (
    <div className="relative mt-4">
      <div
        ref={ref}
        tabIndex={overflows ? 0 : -1}
        role={overflows ? "group" : undefined}
        aria-label={overflows ? "Review text, scroll for more" : undefined}
        className={`scrollbar-none ${scrollClass} [scrollbar-width:none] overflow-y-auto overscroll-contain pb-10 [-ms-overflow-style:none] focus:outline-none [&::-webkit-scrollbar]:hidden ${
          overflows
            ? "focus-visible:outline-teal-07 focus-visible:rounded-[8px] focus-visible:outline-2 focus-visible:-outline-offset-2"
            : ""
        }`}
      >
        <p className="text-base leading-relaxed text-black/70">{text}</p>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-t from-white to-transparent"
      />
    </div>
  );
}

export function ReviewDialog({ review, onClose }: { review: GoogleReview; onClose: () => void }) {
  const isDesktop = useMediaQuery("(min-width: 768px)", true);

  if (isDesktop) {
    return (
      <DialogPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-60 bg-black/60 md:backdrop-blur-[2px]" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed top-1/2 left-1/2 z-70 w-[min(92vw,560px)] -translate-x-1/2 -translate-y-1/2 rounded-[20px] bg-white p-8 shadow-[0_40px_80px_-24px_rgba(0,0,0,0.35)] outline-none"
          >
            <DialogPrimitive.Title className="sr-only">
              Review from {formatName(review.authorName)}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Close"
              className="bg-teal-07 hover:bg-teal-08 absolute top-4 right-4 flex size-9 cursor-pointer items-center justify-center rounded-full text-white shadow-md transition-colors"
            >
              <X className="size-4" strokeWidth={2.5} />
            </DialogPrimitive.Close>
            <ReviewHeader review={review} large />
            <ReviewScrollText text={review.text} scrollClass="max-h-[55vh]" />
            <div className="mt-6">
              <GoogleMark large />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <DrawerPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 md:backdrop-blur-[2px]" />
        <DrawerPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md rounded-t-[24px] bg-white outline-none">
          <DrawerPrimitive.Title className="sr-only">
            Review from {formatName(review.authorName)}
          </DrawerPrimitive.Title>
          <DrawerPrimitive.Description className="sr-only">
            Full Google review text.
          </DrawerPrimitive.Description>
          <div className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-black/15" />
          <div className="p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
            <ReviewHeader review={review} large />
            <ReviewScrollText text={review.text} scrollClass="max-h-[55dvh] touch-pan-y" />
            <div className="mt-6">
              <GoogleMark large />
            </div>
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

const AVATAR_COLOURS = ["bg-navy-01", "bg-brand-orange-03"];

export function ReviewCard({
  review,
  index = 0,
  fullText = false,
  tall = false,
  buttonRef,
  tabIndex,
  onKeyDown,
  cardColor,
}: {
  review: GoogleReview;
  index?: number;
  fullText?: boolean;
  tall?: boolean;
  cardColor?: string;
  buttonRef?: (el: HTMLButtonElement | null) => void;
  tabIndex?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
}) {
  const [open, setOpen] = useOpenWithFocus(false);
  const truncateLimit = tall ? 280 : TRUNCATE_AT;
  const truncated = !fullText && review.text.length > truncateLimit;
  const display = truncated ? `${review.text.slice(0, truncateLimit)}…` : review.text;

  const heightClass = fullText ? "" : tall ? "h-90" : "h-80";
  const containerClass = `relative flex w-full flex-col rounded-[12px] border p-5 transition-colors ${
    cardColor ? "" : "border-white bg-white"
  } ${heightClass}`;

  return (
    <div
      className={containerClass}
      style={cardColor ? { backgroundColor: cardColor, borderColor: cardColor } : undefined}
    >
      <div className="min-h-0 flex-1 overflow-hidden">
        <ReviewHeader review={review} avatarClass={AVATAR_COLOURS[index % AVATAR_COLOURS.length]} />
        <p className="mt-3 text-[16px] leading-relaxed text-black/60">{display}</p>
      </div>
      <div className="mt-4 flex shrink-0 items-center justify-between gap-2">
        <GoogleMark />
        {truncated && (
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpen(true)}
            tabIndex={tabIndex}
            onKeyDown={onKeyDown}
            aria-label={`Read more from ${formatName(review.authorName)}`}
            className="text-teal-08 focus-visible:outline-teal-07 cursor-pointer rounded px-1 py-1 text-sm font-bold transition-colors hover:underline focus-visible:outline-2 focus-visible:-outline-offset-2"
          >
            Read more
          </button>
        )}
      </div>
      {open && <ReviewDialog review={review} onClose={() => setOpen(false)} />}
    </div>
  );
}
